import {
  FragmentConfig,
  FragmentName,
  FragmentStateUpdateOptions,
  FragmentFormInputOptionKey,
  FragmentFormInputOptionValueType,
} from '../types/fragments.types';

export const ORCHESTRATOR_FRAGMENT_ELEMENT_ID = 'anddone-orchestrator-fragment';
export const DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT = 84;
export const DEFAULT_BUTTON_FRAGMENT_IFRAME_HEIGHT = 50;

export const OPTIONAL_FIELD_STAGED_STATUS: FragmentStateUpdateOptions = {
  status: 'staged',
  isValid: true,
};

export const DEFAULT_ALLOWED_FORM_OPTIONS: FragmentFormInputOptionKey[] = ['hideLabels'];
export const ACCOUNT_NUMBER_ALLOWED_OPTIONS: FragmentFormInputOptionKey[] = [
  ...DEFAULT_ALLOWED_FORM_OPTIONS,
  'displayStacked',
];
export const CONTACT_INFORMATION_ALLOWED_OPTIONS: FragmentFormInputOptionKey[] = [
  ...DEFAULT_ALLOWED_FORM_OPTIONS,
  'displayStacked',
];

export const FRAGMENT_FORM_INPUT_OPTION_TYPES: Record<
  FragmentFormInputOptionKey,
  FragmentFormInputOptionValueType
> = {
  hideLabels: 'boolean',
  displayStacked: 'boolean',
};

export const INPUT_FRAGMENT_ALLOWED_COMMANDS = [
  'handshake-response',
  'reset-fragment',
  'submit-fragment',
  'set-values',
] as const;

export const PAYMENT_FRAGMENT_ALLOWED_COMMANDS = [
  ...INPUT_FRAGMENT_ALLOWED_COMMANDS,
  'set-payment-method',
] as const;

export const FULL_CHECKOUT_FRAGMENT_ALLOWED_COMMANDS = [
  ...PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
  'update-payment-authorization',
  'update-fees-and-totals',
  'update-metadata-display',
] as const;

export const ORCHESTRATOR_FRAGMENT_ALLOWED_COMMANDS = [
  'handshake-response',
  'add-orchestrator-log',
  'transaction-assemble',
  'status-update',
] as const;

export const FRAGMENT_CONFIG: Map<FragmentName, FragmentConfig> = new Map([
  // --------------- single input contact information fragments ----------------
  [
    'contact-name',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'customerName',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'company-name',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'companyName',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'email',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'email',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  // --------------- single input address fragments ----------------
  [
    'address1',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'address1',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'address2',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'address2',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'country',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'country',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'city',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'city',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'phone',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'phone',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'state',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'state',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'zip-code',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'zipCode',
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],

  // --------------- Card payment single input fragments ----------------
  [
    'card',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'card',
      paymentMethodTypeAllowed: 'card',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'cvv',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'cvv',
      paymentMethodTypeAllowed: 'card',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'expiration',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'expiration',
      paymentMethodTypeAllowed: 'card',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'cardholder-name',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'cardholderName',
      paymentMethodTypeAllowed: 'card',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],

  // --------------- ACH single input fragments ----------------
  [
    'account-holder-name',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'accountHolderName',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'account-number',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: ACCOUNT_NUMBER_ALLOWED_OPTIONS,
      input: 'accountNumber',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'routing-number',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'routingNumber',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'bank-name',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'bankName',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'banking-type',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'bankingType',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'account-type',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: DEFAULT_SINGLE_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'single',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      input: 'accountType',
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  // --------------- multi-input fragments ----------------
  [
    'contact-information',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: 220,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: CONTACT_INFORMATION_ALLOWED_OPTIONS,
      inputs: ['companyName', 'contactName', 'email', 'phone'],
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'ach-payment',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: 440,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: [
        'accountHolderName',
        'accountNumber',
        'accountNumberVerify',
        'routingNumber',
        'bankName',
        'bankingType',
        'accountType',
      ],
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'address-full',
    {
      hasSensitiveData: false,
      canSetValues: true,
      isPaymentFragment: false,
      defaultFragmentHeight: 300,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: ['address1', 'address2', 'city', 'state', 'zipCode', 'country'],
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'card-payment',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: 220,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: ['card', 'expiration', 'cvv', 'cardholderName'],
      paymentMethodTypeAllowed: 'card',
      allowedCommands: PAYMENT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],

  // --------------- Full checkout fragments ----------------
  [
    'full-card-checkout',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: 600,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: [
        'card',
        'expiration',
        'cvv',
        'cardholderName',
        'address1',
        'address2',
        'city',
        'state',
        'zipCode',
        'country',
        'companyName',
        'contactName',
        'email',
        'phone',
      ],
      paymentMethodTypeAllowed: 'card',
      allowedCommands: FULL_CHECKOUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'full-ach-checkout',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: 600,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: [
        'accountHolderName',
        'accountNumber',
        'accountNumberVerify',
        'routingNumber',
        'bankName',
        'bankingType',
        'accountType',
        'address1',
        'address2',
        'city',
        'state',
        'zipCode',
        'country',
        'companyName',
        'contactName',
        'email',
        'phone',
      ],
      paymentMethodTypeAllowed: 'ach',
      allowedCommands: FULL_CHECKOUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'full-multi-channel-checkout',
    {
      hasSensitiveData: true,
      canSetValues: false,
      isPaymentFragment: true,
      defaultFragmentHeight: 220,
      inputMode: 'multiple',
      canSubmit: true,
      allowedOptions: DEFAULT_ALLOWED_FORM_OPTIONS,
      inputs: [
        'accountHolderName',
        'accountNumber',
        'accountNumberVerify',
        'routingNumber',
        'bankName',
        'bankingType',
        'accountType',
        'card',
        'expiration',
        'cvv',
        'cardholderName',
        'address1',
        'address2',
        'city',
        'state',
        'zipCode',
        'country',
        'companyName',
        'contactName',
        'email',
        'phone',
      ],
      paymentMethodTypeAllowed: 'any',
      allowedCommands: FULL_CHECKOUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  // --------------- functional fragments ----------------
  [
    'submit',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: DEFAULT_BUTTON_FRAGMENT_IFRAME_HEIGHT,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: ['handshake-response'] as const,
    },
  ],
  [
    'payment-method-selector',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: 120,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: INPUT_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
  [
    'fees-and-totals',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: 161,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: ['handshake-response', 'update-fees-and-totals'] as const,
    },
  ],
  [
    'metadata-display',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: 161,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: ['handshake-response', 'update-metadata-display'] as const,
    },
  ],
  [
    'payment-authorization',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: 122,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: ['handshake-response', 'update-payment-authorization'] as const,
    },
  ],
  // --------------- core orchestrator fragment ----------------
  [
    'orchestrator',
    {
      hasSensitiveData: false,
      canSetValues: false,
      isPaymentFragment: false,
      defaultFragmentHeight: 800,
      inputMode: 'none',
      canSubmit: false,
      paymentMethodTypeAllowed: 'any',
      allowedCommands: ORCHESTRATOR_FRAGMENT_ALLOWED_COMMANDS,
    },
  ],
]);
